import React, {useCallback, useContext} from 'react';
import {useLocation} from 'react-router-dom';

import {ConfirmationContext} from 'src/contexts/confirmation-context';
import {useEventTracking} from 'src/hooks/useEventTracking';

import {Group} from '@pb/kit-component-group';
import {Icons, Sizes} from '@pb/kit-component-icon';
import {BasicIconButton} from '@pb/kit-component-icon-button';
import {Info} from '@pb/kit-component-info';
import Label from '@pb/kit-component-label';
import {Tooltip} from '@pb/kit-component-popups';
import {useShown} from '@pb/kit-hooks-use-shown';

import {EditSfdcIdModal} from './edit-sfdc-id-modal.component';

import type {SaveOptions} from 'src/components/save-with-reason/save-with-reason-button';


const getCRMLink = (id: string) => `https://pitchbookdata.lightning.force.com/lightning/r/Contact/${id}/view`;

const SALESFORCE_INFO = 'ID of the user in the PitchBook Salesforce environment';
const LABEL = 'PB Salesforce ID';
const EDIT_TOOLTIP = 'Edit PB Salesforce ID';
const UNSAVED_CHANGES_MESSAGE = 'You have unsaved changes. Please save or discard them before you edit the PB Salesforce ID.';

type Props = {
    sfdcId: string,
    editDisabled?: boolean,
    onSave: (sfdcId: string, options?: SaveOptions) => Promise<string | undefined>,
};

const wrapContent = (content: React.ReactNode) => (
    <div style={{width: 300}}>
        <Label>
            <Info
                dropdownProps={{
                    style: {
                        width: '265px',
                    },
                }}
                type={{popover: SALESFORCE_INFO}}>
                {LABEL}
            </Info>
        </Label>
        {content}
    </div>
);

export const CRMLink = ({sfdcId, editDisabled, onSave}: Props) => {
    const {pathname} = useLocation();
    const {userFieldChange} = useEventTracking();
    const {changedChunks} = useContext(ConfirmationContext);
    const {shown, onClick, onClose} = useShown();

    const onLinkClick = useCallback(() => {
        userFieldChange({
            section: 'User Details',
            viewType: 'Existed',
            parameterName: LABEL,
        });
    }, [userFieldChange]);

    const onEditClick = useCallback(() => {
        onLinkClick();
        onClick();
    }, [onLinkClick, onClick]);

    if (pathname.endsWith('/create-user')) {
        return null;
    }

    const value = sfdcId
        ? <b><a target="_blank" onClick={onLinkClick} href={getCRMLink(sfdcId)}>{sfdcId}</a></b>
        : <span>--</span>;

    if (editDisabled) {
        return wrapContent(value);
    }

    return wrapContent(
        <Group gap="xs">
            {value}
            <Tooltip
                color="light"
                anchor={({anchorRef, getAnchorProps}) => (
                    <BasicIconButton
                        ref={anchorRef}
                        {...getAnchorProps()}
                        aria-label={EDIT_TOOLTIP}
                        disabled={!!changedChunks.length}
                        iconProps={{
                            type: Icons.PENCIL,
                            size: Sizes.Size_14,
                        }}
                        onClick={onEditClick}
                    />
                )}>
                {changedChunks.length ? UNSAVED_CHANGES_MESSAGE : EDIT_TOOLTIP}
            </Tooltip>
            <EditSfdcIdModal {...{shown, sfdcId, onClose, onSave}}/>
        </Group>,
    );
};
